// src/controllers/egresoCabController.js

import { getEgresoCabs, getEgresoCabsWithExpandedData, createEgresoCab, updateEgresoCab, deleteEgresoCab } from '../models/egresoCabModel.js';
import { getEgresoDets } from '../models/egresoDetModel.js';
import Swal from 'sweetalert2';
import pb from '../lib/pocketbase.js';
import { toasts } from "svelte-toasts";

let previousValues = new Map(); // Almacena los valores anteriores de los registros

// Utilidad para evitar demasiados toasts (throttle/deduplicación)
const toastHistory = new Map();
function showToastWithThrottle({ title, description, ...options }) {
  const key = title + description;
  const now = Date.now();
  const THROTTLE_TIME = 3000; // 3 segundos
  if (now - (toastHistory.get(key) || 0) > THROTTLE_TIME) {
    toasts.add({ title, description, ...options });
    toastHistory.set(key, now);
  }
}

// Obtener lista de egresos paginados SIN los detalles de egresoDet
export async function fetchEgresoCabs(page, perPage) {
  try {
    const response = await getEgresoCabs(page, perPage);

    return {
      egresoCab: response.items, // Solo devolvemos la lista de egresos sin detalles
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    console.error("Error fetching egresoCabs:", error);
    Swal.fire('Error', 'No se pudieron cargar los egresos', 'error');
    return null;
  }
}

// Obtener lista de egresos con datos expandidos (picker, escolta, corredor)
export async function fetchEgresoCabsWithExpandedData(filters = {}) {
  try {
    const response = await getEgresoCabsWithExpandedData(filters);

    return {
      egresoCab: response.items,
      totalRecords: response.totalItems,
      currentPage: response.page,
    };
  } catch (error) {
    console.error("Error fetching egresoCabs with expanded data:", error);
    Swal.fire('Error', 'No se pudieron cargar los egresos', 'error');
    return null;
  }
}

// Guardar o actualizar un egreso
export async function saveEgresoCab(egresoCab, currentEgresoCabId) {
  try {
    if (currentEgresoCabId) {
      await updateEgresoCab(currentEgresoCabId, egresoCab);
      showToastWithThrottle({
        title: 'Éxito',
        description: 'Egreso actualizado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true
      });
    } else {
      await createEgresoCab(egresoCab);
      showToastWithThrottle({
        title: 'Éxito',
        description: 'Egreso creado exitosamente',
        duration: 3000,
        placement: 'bottom-right',
        type: 'success',
        theme: 'dark',
        showProgress: true
      });
    }
  } catch (error) {
    console.error("Error saving egresoCab:", error);
    showToastWithThrottle({
      title: 'Error',
      description: 'Ocurrió un error al guardar el egreso',
      duration: 3000,
      placement: 'bottom-right',
      type: 'error',
      theme: 'dark',
      showProgress: true
    });
  }
}

// Eliminar un egreso
export async function removeEgresoCab(id) {
  try {
    await deleteEgresoCab(id);
    showToastWithThrottle({
      title: 'Éxito',
      description: 'Egreso eliminado exitosamente.',
      duration: 3000,
      placement: 'bottom-right',
      type: 'success',
      theme: 'dark',
      showProgress: true
    });
  } catch (error) {
    console.error("Error deleting egresoCab:", error);
    Swal.fire('Error', 'No se pudo eliminar el egreso', 'error');
  }
}

// Mapeo de estados a tipos de toast
const statusToToastType = {
  NOT_READY: 'error',
  PACKING: 'warning',
  PARTIALLY_READY: 'warning',
  READY: 'info',
  ON_ROUTE: 'info',
  DELIVERED: 'success',
  RETURNED: 'error',
  DEFAULT: 'info'
};

// Obtiene un mensaje descriptivo según el estado
function getStatusMessage(status) {
  const messages = {
    NOT_READY: 'No listo para entrega',
    PACKING: 'En proceso de empaque',
    PARTIALLY_READY: 'Parcialmente listo',
    READY: 'Listo para entrega',
    ON_ROUTE: 'En ruta de entrega',
    DELIVERED: 'Entregado al cliente',
    RETURNED: 'Devuelto',
    DEFAULT: 'Estado actualizado'
  };
  return messages[status] || messages.DEFAULT;
}

// Suscripción en tiempo real a cambios en la colección 'egresoCab'
export function subscribeToEgresoCabs(handleSearch, debounceMs = 800) {
  let refreshTimeout;

  const scheduleRefresh = () => {
    clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(() => {
      handleSearch();
    }, debounceMs);
  };

  pb.collection('egresoCab').subscribe('*', async (e) => {
    console.log('Egreso event received:', e); // Debug log

    /*
    if (e.action === 'create') {
      const audio = new Audio('/assets/sounds/notification-2.wav');
      audio.play()
        .then(() => console.log("Sonido reproducido exitosamente"))
        .catch((err) => console.error("Error al reproducir el sonido:", err));
      
      // Create notification in PocketBase
      try {
        const notification = await pb.collection('notificacion').create({
          mensaje: `Se ha creado un nuevo egreso: ${e.record.nombre || 'Sin descripción'}`,
          sta: true,
          user_id: pb.authStore.model?.id || null
        });
        console.log('Notification created:', notification); // Debug log
      } catch (error) {
        console.error("Error creating notification:", error);
      }
      
      // Añadir toast para nuevos registros
      const status = e.record.sta || 'DEFAULT';
      showToastWithThrottle({
        title: 'Nuevo Egreso',
        description: `Se ha creado un nuevo egreso: ${e.record.nombre || 'Sin descripción'}`,
        duration: 4000,
        placement: 'bottom-right',
        type: statusToToastType[status],
        theme: 'dark',
        showProgress: true
      });
    }
    */

    if (e.action === 'update') {
      const currentRecord = e.record;
      const previousSta = previousValues.get(currentRecord.id)?.sta;

      console.log('previousSta:', previousSta);
      console.log('currentRecord.sta:', currentRecord.sta);
      if (previousSta !== currentRecord.sta) {
        const audio = new Audio('/assets/sounds/notification-1.wav');
        audio.play()
          .then(() => console.log("Sonido reproducido exitosamente"))
          .catch((err) => console.error("Error al reproducir el sonido:", err));
        
        // Create notification in PocketBase for status change
        try {
          const notification = await pb.collection('notificacion').create({
            mensaje: `Cambio de estado en egreso #${currentRecord.numero}: ${currentRecord.nombre} - ${getStatusMessage(currentRecord.sta)}`,
            sta: true,
            user_id: pb.authStore.model?.id || null
          });
          console.log('Status change notification created:', notification); // Debug log
        } catch (error) {
          console.error("Error creating notification:", error);
        }
        
        // Añadir toast coloreado según el estado actual
        const status = currentRecord.sta || 'DEFAULT';
        showToastWithThrottle({
          title: 'Cambio de Estado #' + currentRecord.numero,
          description: currentRecord.nombre + ' - ' + getStatusMessage(status),
          duration: 4000,
          placement: 'bottom-right',
          type: statusToToastType[status],
          theme: 'dark',
          showProgress: true
        });
      }

      // Actualiza el valor anterior en el mapa
      previousValues.set(currentRecord.id, { ...currentRecord });
    }

    scheduleRefresh();
  });
}

// Desuscripción de los eventos en tiempo real
export function unsubscribeFromEgresoCabs() {
  pb.collection('egresoCab').unsubscribe('*');
}

// Buscar egresos por descripción con paginación
export async function searchEgresoCabs(query, page = 1, perPage = 10) {
  try {
    const response = await pb.collection('egresoCab').getList(page, perPage, {
      filter: `descrip ~ "${query}"`,
    });
    return {
      egresoCab: response.items,
      totalRecords: response.totalItems,
      currentPage: response.currentPage,
    };
  } catch (error) {
    console.error("Error searching egresoCabs:", error);
    Swal.fire('Error', 'No se pudo realizar la búsqueda', 'error');
    return null;
  }
}